import { InitialUser } from '@eagle/core-data-types';
import { isAxiosError } from 'axios';
import { get } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CacheDataTypes, Undefinable } from '../types';
import { getErrorReason } from '../util';

export type ErrorHandler = (
  err: unknown,
  dataType: string,
  display: Undefinable<string>,
  fallback?: string,
) => void;

interface ApiErrorHandlers {
  handleUpdateError: ErrorHandler;
  handleCreateError: ErrorHandler;
  handleGetError: ErrorHandler;
  handleDeleteError: ErrorHandler;
  handleAccountCreateError: (err: unknown, display: string) => void;
}

export const useApiErrorHandler = (): ApiErrorHandlers => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['common']);

  const handleUpdateError = useCallback((
    err: unknown,
    dataType: string,
    display: Undefinable<string>,
    fallback?: string,
  ) => {
    const { path, reason } = getErrorReason(err);

    if (path && reason) {
      enqueueSnackbar(
        t('common:common.hint.update-failure-v2', { type: dataType, path, reason }),
        { variant: 'error' },
      );
    } else {
      enqueueSnackbar(
        fallback ?? t('common:common.hint.update-failure', { entity: display }),
        { variant: 'error' },
      );
    }
  }, [enqueueSnackbar, t]);

  const handleCreateError = useCallback((
    err: unknown,
    dataType: string,
    display: Undefinable<string>,
    fallback?: string,
  ) => {
    const { path, reason } = getErrorReason(err);

    if (path && reason) {
      enqueueSnackbar(
        t('common:common.hint.create-failure-v2', { entityType: dataType, path, reason }),
        { variant: 'error' },
      );
    } else {
      enqueueSnackbar(
        fallback ?? t('common:common.hint.create-failure', { display }),
        { variant: 'error' },
      );
    }
  }, [enqueueSnackbar, t]);

  const handleGetError = useCallback((
    err: unknown,
    dataType: string,
    _display: Undefinable<string>,
    fallback?: string,
  ) => {
    const { path, reason } = getErrorReason(err);

    if (path && reason) {
      enqueueSnackbar(
        t('common:common.hint.get-failure-v2', { type: dataType, path, reason }),
        { variant: 'error' },
      );
    } else if (fallback) {
      enqueueSnackbar(fallback, { variant: 'error' });
    }
  }, [enqueueSnackbar, t]);

  const handleDeleteError = useCallback((
    err: unknown,
    dataType: string,
    display: Undefinable<string>,
    fallback?: string,
  ) => {
    const { path, reason } = getErrorReason(err);

    if (path && reason) {
      enqueueSnackbar(
        t('common:common.hint.delete-failure-v2', { type: dataType, path, reason }),
        { variant: 'error' },
      );
    } else {
      enqueueSnackbar(
        fallback ?? t('common:component.delete-entity-dialog.hint.failure', { display }),
        { variant: 'error' },
      );
    }
  }, [enqueueSnackbar, t]);

  const handleAccountCreateError = useCallback((
    err: unknown,
    display: string,
  ) => {
    const { path, reason } = getErrorReason(err);
    const users = isAxiosError(err) && typeof err.config?.data === 'string' ? (JSON.parse(err.config.data) as { users?: InitialUser[] }).users ?? [] : [];

    if (reason === 'conflicting-value' && typeof path === 'string' && /^users\.\[\d+\]\.email$/.test(path)) {
      const email = get({ users }, path) as string;
      enqueueSnackbar(t('common:common.hint.create-account-failure-v2', { entityType: CacheDataTypes.ACCOUNT, emailAddress: email }), { variant: 'error' });
    }
    else {
      if (isAxiosError<{ error?: { type?: string } }>(err) && err.response?.data.error?.type === 'ConflictError' && users.length === 1) {
        enqueueSnackbar(t('common:common.hint.create-account-failure-v2', { entityType: CacheDataTypes.ACCOUNT, emailAddress: users[0].display }), { variant: 'error' });
      }
      else {
        handleCreateError(
          err,
          CacheDataTypes.ACCOUNT,
          display,
        );
      }
    }
  }, [enqueueSnackbar, handleCreateError, t]);

  return { handleUpdateError, handleCreateError, handleGetError, handleDeleteError, handleAccountCreateError };
};
